import { useState, useRef, useCallback } from "react";
import { type DisciplineSlug } from "@jumpbean/shared";
import { getCharacterVideoUrl, CharacterImage, GLOW_COLORS } from "./CharacterImage";

export interface IntroVideoPlayerProps {
  /** The discipline whose character video to show */
  discipline: DisciplineSlug;
  /** Called when the video ends or is skipped */
  onComplete?: () => void;
  /** Whether to show the video in a modal overlay */
  modal?: boolean;
  /** Whether to autoplay the video */
  autoPlay?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * IntroVideoPlayer - Plays character introduction videos
 *
 * Features:
 * - Shows a play button thumbnail before playing
 * - Full-screen modal mode available
 * - Skip button always available
 * - Character-themed styling
 * - Graceful handling when video unavailable
 */
export function IntroVideoPlayer({
  discipline,
  onComplete,
  modal = false,
  autoPlay = false,
  className = "",
}: IntroVideoPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [hasError, setHasError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const videoUrl = getCharacterVideoUrl(discipline);
  const glowColor = GLOW_COLORS[discipline];

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
    videoRef.current?.play();
  }, []);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    onComplete?.();
  }, [onComplete]);

  const handleSkip = useCallback(() => {
    videoRef.current?.pause();
    setIsPlaying(false);
    onComplete?.();
  }, [onComplete]);

  const handleError = useCallback(() => {
    setHasError(true);
  }, []);

  // If video failed to load, show character image instead
  if (hasError) {
    return (
      <div
        className={`
          relative rounded-2xl overflow-hidden
          bg-gradient-to-br from-neutral-800 to-neutral-900
          flex flex-col items-center justify-center p-8
          ${className}
        `}
      >
        <CharacterImage
          discipline={discipline}
          expression="happy"
          size="hero"
          floating
          glowing
        />
        <p className="mt-4 font-body text-body-lg text-neutral-400 text-center">
          Video not available
        </p>
      </div>
    );
  }

  // Modal overlay mode
  if (modal && isPlaying) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90">
        <div className="relative w-full max-w-4xl mx-4">
          <video
            ref={videoRef}
            src={videoUrl}
            className="w-full rounded-2xl shadow-2xl"
            autoPlay
            playsInline
            onEnded={handleEnded}
            onError={handleError}
            style={{
              boxShadow: `0 0 60px ${glowColor}, 0 0 120px ${glowColor}`,
            }}
          />
          <button
            onClick={handleSkip}
            className="
              absolute top-4 right-4
              px-6 py-3
              bg-white/20 backdrop-blur-sm
              rounded-full
              font-display font-bold text-white
              hover:bg-white/30 transition-colors
              focus:outline-none focus-visible:ring-4 focus-visible:ring-white/50
            "
          >
            Skip
          </button>
        </div>
      </div>
    );
  }

  // Inline player with thumbnail
  return (
    <div
      className={`
        relative rounded-2xl overflow-hidden
        bg-gradient-to-br from-neutral-800 to-neutral-900
        group cursor-pointer
        ${className}
      `}
      onClick={!isPlaying ? handlePlay : undefined}
    >
      {!isPlaying ? (
        // Thumbnail state with play button
        <div className="relative aspect-video">
          {/* Character preview */}
          <div className="absolute inset-0 flex items-center justify-center">
            <CharacterImage
              discipline={discipline}
              expression="happy"
              size="xl"
              floating
              glowing
            />
          </div>

          {/* Decorative background particles */}
          <VideoThumbnailParticles discipline={discipline} />

          {/* Play button overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/10 transition-colors">
            <div
              className="
                w-20 h-20
                rounded-full
                bg-white/90 backdrop-blur-sm
                flex items-center justify-center
                shadow-xl
                group-hover:scale-110 transition-transform
              "
              style={{
                boxShadow: `0 0 30px ${glowColor}`,
              }}
            >
              <PlayIcon className="w-10 h-10 ml-1 text-neutral-800" />
            </div>
          </div>

          {/* "Watch Intro" label */}
          <div className="absolute bottom-4 left-0 right-0 flex justify-center">
            <span
              className="
                px-4 py-2
                bg-black/50 backdrop-blur-sm
                rounded-full
                font-display font-bold text-body-md text-white
              "
            >
              Watch Intro
            </span>
          </div>
        </div>
      ) : (
        // Playing state
        <div className="relative aspect-video">
          <video
            ref={videoRef}
            src={videoUrl}
            className="w-full h-full object-cover"
            autoPlay
            playsInline
            onEnded={handleEnded}
            onError={handleError}
          />
          <button
            onClick={handleSkip}
            className="
              absolute top-4 right-4
              px-4 py-2
              bg-black/50 backdrop-blur-sm
              rounded-full
              font-display font-bold text-body-sm text-white
              hover:bg-black/70 transition-colors
              focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50
            "
          >
            Skip
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Decorative particles for the video thumbnail
 */
function VideoThumbnailParticles({
  discipline,
}: {
  discipline: DisciplineSlug;
}) {
  const colors = getParticleColors(discipline);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {Array.from({ length: 15 }).map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full animate-float"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            width: `${4 + Math.random() * 8}px`,
            height: `${4 + Math.random() * 8}px`,
            background: colors[i % colors.length],
            opacity: 0.4 + Math.random() * 0.4,
            animationDelay: `${Math.random() * 3}s`,
            animationDuration: `${3 + Math.random() * 2}s`,
          }}
        />
      ))}
    </div>
  );
}

function getParticleColors(discipline: DisciplineSlug): string[] {
  const colorSets: Record<DisciplineSlug, string[]> = {
    math: ["#E8A0FF", "#F0C4FF", "#FFD93D", "#C77DDB"],
    spelling: ["#90EE90", "#B8F5C8", "#FFB8D0", "#6BCB77"],
    patterns: ["#FF6B6B", "#FFB347", "#FFE66D", "#98D89B", "#74B9FF", "#A29BFE"],
    typing: ["#4A90D9", "#7EB3E8", "#FFD700", "#3A75B0"],
    programming: ["#FF8C42", "#E8C4A0", "#00D4FF", "#B87333"],
  };
  return colorSets[discipline];
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

export default IntroVideoPlayer;
